import clsx from 'clsx';
import { getDate, getMonth, getYear } from 'date-fns';
import { isEmpty, toNumber } from 'lodash';
import { array, func, object } from 'prop-types';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { Icon } from 'components';
import { getValidDate } from 'helpers';
import { useSafeState } from 'hooks';

const ParticipantsList = ({ passengerCategory, participants, onAddParticipant, openAddParticipantModal }) => {
  const { t } = useTranslation();
  /* State of the selected participant in the list. Only one participant can be selected while the modal is opened */
  const [selectedPartipantRow, setSelectedParticipantRow] = useSafeState(null);
  const [participantList, setParticipantList] = useSafeState(participants);
  const storedReturnDate = useSelector((state) => state.travelPurchase.returnDate);

  const filteredParticipants = useMemo(() => {
    const { minOld, maxOld } = passengerCategory;

    return participantList.filter((participant) => {
      const birthDate = getValidDate(participant.birthDate);
      const returnDateObj = new Date(storedReturnDate);
      let age = getYear(returnDateObj) - getYear(birthDate);
      const monthDifference = getMonth(returnDateObj) - getMonth(birthDate);
      const dayDifference = getDate(returnDateObj) - getDate(birthDate);

      // Adjust age if the participant's birthday hasn't occurred yet this year
      if (monthDifference < 0 || (monthDifference === 0 && dayDifference < 0)) {
        age--;
      }

      // Check age against minOld and maxOld
      const isMinAgeValid = minOld ? age >= toNumber(minOld) : true;
      const isMaxAgeValid = maxOld ? age < toNumber(maxOld) : true;

      return isMinAgeValid && isMaxAgeValid;
    });
  }, [passengerCategory, participantList]);

  const handleParticipantRowClick = (participant) => {
    const updatedParticipants = participantList.map((p) => {
      if (selectedPartipantRow && participant.serialNumber === selectedPartipantRow.serialNumber) {
        /* Toggle value of 'selected' to check/uncheck a participant  */
        if (p.serialNumber === participant.serialNumber) {
          return { ...p, selected: !p.selected };
        }
        /* Set all the other participant of the list to selected = false because only one participant can be selected */
        return { ...p, selected: p.locked };
      } else {
        /* Cannot unselect locked participant */
        if (participant.locked) return p;
        /* If a participant is already selected, set the selected value to true only for the clicked participant */
        if (p.serialNumber === participant.serialNumber) {
          return { ...p, selected: true };
        }
        return { ...p, selected: p.locked };
      }
    });

    setSelectedParticipantRow(participant.selected ? null : { ...participant, selected: true });

    setParticipantList(updatedParticipants);
  };

  return (
    <>
      <div className="grid grid-cols-3 border-b-2 col-span-3 pt-6 pb-2 mb-4">
        <p className="col-span-1 uppercase text-gray-400 text-sm md:text-base">
          {t('purchase:configuration.lastName')}
        </p>
        <p className="col-span-1 uppercase text-gray-400 text-sm md:text-base">
          {t('purchase:configuration.firstName')}
        </p>
        <p className="col-span-1 uppercase text-gray-400 text-sm md:text-base">
          {t('purchase:configuration.birthDate')}
        </p>
      </div>

      {filteredParticipants?.length === 0 && <p>{t('purchase:configuration.noRightHolder')}</p>}

      {filteredParticipants?.map((participant, index) => (
        <div
          key={`participant-${index}`}
          className={clsx(
            'grid grid-cols-3 gap-4 py-2 border-b relative',
            !participant.locked && 'hover:bg-gray-50 cursor-pointer'
          )}
        >
          {/*
            CSS hidden button to select the participant on click on the row
            If participant is locked, it can not be unselected so the button is not displayed
          */}
          {!participant.locked && (
            <button
              type="button"
              className="absolute inset-0 opacity-0 z-10"
              onClick={() => handleParticipantRowClick(participant)}
            >
              <span className="sr-only">
                {t('purchase:configuration.selectParticipant', {
                  fullName: `${participant.name} ${participant.firstName}`
                })}
              </span>
            </button>
          )}

          <div className="col-span-1 flex items-center gap-2">
            <div className="fake-checkbox">
              {participant.selected && <Icon name="check" classes="text-primary-500" />}
            </div>

            <p className="truncate">{participant.name}</p>
          </div>

          <p className="col-span-1 truncate">{participant.firstName}</p>
          <p className="col-span-1">{participant.birthDate}</p>
        </div>
      ))}

      <button type="button" className="btn outlined small my-8 gap-2" onClick={openAddParticipantModal}>
        <Icon name="plus" />
        <span className="text-sm md:text-base">{t('purchase:configuration.addParticipant')}</span>
      </button>

      <button
        type="button"
        className="btn ml-auto"
        disabled={isEmpty(selectedPartipantRow)}
        onClick={() => onAddParticipant(selectedPartipantRow, passengerCategory)}
      >
        {t('buttons.validate')}
      </button>
    </>
  );
};

ParticipantsList.propTypes = {
  participants: array,
  passengerCategory: object,
  onAddParticipant: func,
  openAddParticipantModal: func
};

export default ParticipantsList;
